// src/NewRecordPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../scripts/api";

import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// --- Status list used by ColorCode dropdown (matches your status.js)
const STATUS_BY_CODE = {
  0: { bg: '#FFFFFF', label: 'UNASSIGNED' },
  1: { bg: '#C757FF', label: 'SOLD' },
  2: { bg: '#FFD966', label: 'RENTED OUT' },
  3: { bg: '#69e7a2ff', label: 'IN STOCK - CALGARY' },
  4: { bg: '#92D050', label: 'IN TRANSIT TO CALGARY' },
  5: { bg: '#92D050', label: 'IN TRANSIT TO CUSTOMER' },
  6: { bg: '#FF0000', label: 'ALERT' },
  7: { bg: '#4183e6ff', label: 'TO ORDER' },
  8: { bg: '#FFCCFF', label: 'ON ORDER' },
  9: { bg: '#00B050', label: 'STOCK & PDI - IN CALGARY' },
  10:{ bg: '#ce951bff', label: 'DEMO' }, 
  11:{ bg: '#1b54ceff', label: 'CHASSIS ALLOCATION SLOT' },
  12:{ bg: '#0a2479ff', label: 'READY AT FACTORY' },
  13: { bg: '#FF0000', label: 'IN TRANSIT - BROKE DOWN' },
};

const COLOR_CODE_OPTIONS = Object.entries(STATUS_BY_CODE)
  .map(([code, v]) => ({ v: String(code), label: `${code} — ${v.label}` }))
  .sort((a, b) => Number(a.v) - Number(b.v));

const CATEGORY_OPTIONS = [
  { v: "", label: "(blank)" },
  { v: "landfill", label: "Landfill" },
  { v: "sewer", label: "Sewer equipment" },
  { v: "sweeper", label: "Sweeper" },
  { v: "refuse", label: "Refuse" },
  { v: "municipal", label: "Municipal" },
  { v: "mrr", label: "MRR" },
  { v: "Rail", label: "Rail" },
  { v: "SnowPlow", label: "Snow Plow" },
];

// columns that are DATE in SQLite
const DATE_FIELDS = new Set([
  "customer_po_date",
  "factory_order_date",
  "chassis_build_date",
  "chassis_arrive_eta",
  "ready_at_factory",
  "arrive_sna",
  "to_customer",
  "body_status_update",
  "chassis_status_update",
]);

// fields that should be treated as numeric/currency TEXT, NOT booleans
const CURRENCY_FIELDS = new Set([
  "exchange",
  "misc_charges",
  "misc_hours",
  "misc_mileage",
  "misc_exchange",
  "misc_addons",
  "misc_repairs",
  "pdi_charges",
  "freight_charges",
  "fright_charges",
  "body_net_sna",
  "body_cost",
  "chassis_net_sna",
  "chassis_cost",
  "total_net_sna",
  "total_cost",
  "total_sell",
  "surcharge",
  "tarrif",
]);

// flags rendered as checkboxes
const CHECKBOX_FIELDS = new Set([
  "is_chassis",
  "is_body",
  "is_attachment",
  "is_equipment",
  "is_stock",
  "is_demo",
  "is_rental",
  "alert",
]);

// GROUPS (copied to match DetailPage)
const GROUPS = [
  {
    title: "Customer Info",
    fields: [
      { k: "customer", label: "Customer Sold To" },
      { k: "customer_po", label: "Customer PO" },
      { k: "sna_customer_po", label: "SNA Customer PO" },
      { k: "customer_po_date", label: "Customer PO Date" },
      { k: "customer_po_update", label: "Customer PO Update" },
      { k: "sent_so", label: "Sent SO" },
      { k: "sign_so", label: "Sign SO" },
      { k: "notes", label: "Notes" },
      { k: "notes2", label: "Notes 2" },
    ],
  },
  {
    title: "Unit Info",
    fields: [
      { k: "rowo", label: "RO / WO #" },
      { k: "desc", label: "Description" },
      { k: "stock_po", label: "Stock PO" },
      { k: "factory_production_so_order", label: "Factory Production SO Order" },
      { k: "factory_order_date", label: "Factory Order Date" },
      { k: "factory_wo", label: "Factory WO" },
      { k: "make_model_size", label: "Model Size" },
      { k: "revd_eng", label: "Revd ENG" },
    ],
  },
  {
    title: "Unit Classification",
    fields: [
      { k: "alert", label: "Alert" },
      { k: "is_chassis", label: "Chassis" },
      { k: "is_body", label: "Body" },
      { k: "is_attachment", label: "Attachment" },
      { k: "is_equipment", label: "Equipment" },
      { k: "is_stock", label: "Stock" },
      { k: "is_demo", label: "Demo" },
      { k: "is_rental", label: "Rental" },
      { k: "unit_category", label: "Category" },
      { k: "color_code", label: "ColorCode" },
      { k: "unit_status", label: "Unit Status" },
    ],
  },
  {
    title: "VIN / Location",
    fields: [
      { k: "chassis_make_model", label: "Chassis Make & Model" },
      { k: "type", label: "Unit Type" },
      { k: "chassis_vin", label: "Chassis VIN" },
      { k: "body_vin", label: "Body VIN" },
      { k: "body_chassis_vin", label: "Body/Chassis VIN (combined)" },
      { k: "unit_location", label: "Location" },
    ],
  },
  {
    title: "SNA Info",
    fields: [
      { k: "sna_body_chassis_po", label: "SNA Body/Chassis PO" },
      { k: "riv_(TEXT)", label: "RIV (Text)" },
    ],
  },
  {
    title: "Finance",
    fields: [
      { k: "exchange", label: "Exchange" },
      { k: "misc_charges", label: "Misc Charges" },
      { k: "misc_hours", label: "Misc Hours" },
      { k: "misc_mileage", label: "Misc KM" },
      { k: "misc_exchange", label: "Misc Exchange" },
      { k: "misc_addons", label: "Misc Add-ons" },
      { k: "misc_repairs", label: "Misc Repairs" },
      { k: "pdi_charges", label: "PDI" },
      { k: "fright_charges", label: "Freight Cost" },
      { k: "body_net_sna", label: "Body Cost" },
      { k: "chassis_net_sna", label: "Chassis Cost" },
      { k: "surcharge", label: "Surcharge" },
      { k: "tarrif", label: "Tariff" },
      { k: "total_net_sna", label: "Total Cost" },
      { k: "total_sell", label: "Total Sell Price" },
    ],
  },
  {
    title: "Updates (Dates)",
    fields: [
      { k: "chassis_status_update", label: "Chassis Status Update" },
      { k: "body_status_update", label: "Body Status Update" },
      { k: "chassis_arrive_eta", label: "Chassis Arrive ETA" },
      { k: "ready_at_factory", label: "Unit Ready at Factory" },
      { k: "arrive_sna", label: "Unit Arrive Calgary" },
      { k: "chassis_build_date", label: "Chassis Build" },
      { k: "to_customer", label: "To Customer" },
      { k: "factory_pick_up", label: "Factory Pickup (driver)" },
      { k: "factory_del_sna", label: "Delivered to SNA (driver)" },
    ],
  },
];

// Match DB keys even if user typed slightly different (case/spacing/underscores)
function findKey(draft, wantedKey) {
  if (!draft) return null;
  if (Object.prototype.hasOwnProperty.call(draft, wantedKey)) return wantedKey;

  const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
  const w = norm(wantedKey);
  for (const k of Object.keys(draft)) {
    if (norm(k) === w) return k;
  }
  return null;
}

function isBoolishValue(v) {
  if (v === 0 || v === 1 || v === true || v === false) return true;
  const s = String(v ?? "").toLowerCase();
  return s === "true" || s === "false" || s === "0" || s === "1" || s === "yes" || s === "no";
}

function toDateInputValue(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return "";
}

function isTruthyFlag(v) {
  const s = String(v ?? "").toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "y";
}

function CustomerAutocomplete({ value, onChange }) {
  const [query,    setQuery]    = useState(value || "");
  const [results,  setResults]  = useState([]);
  const [open,     setOpen]     = useState(false);
  const [loading,  setLoading]  = useState(false);
  const debounce   = useRef(null);
  const wrapRef    = useRef(null);

  // keep input in sync if parent resets value
  useEffect(() => { setQuery(value || ""); }, [value]);

  const search = useCallback((q) => {
    clearTimeout(debounce.current);
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/customers", { params: { q: q.trim(), limit: 12 } });
        setResults(data.rows || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    search(v);
  };

  const select = (row) => {
    const name = row.customer_name || row.name || "";
    setQuery(name);
    onChange(name);
    setResults([]);
    setOpen(false);
  };

  // close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: "relative", minWidth: 0 }}>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Customer Sold To</div>
      <input
        className="input"
        value={query}
        onChange={handleChange}
        onFocus={() => query.trim() && results.length && setOpen(true)}
        placeholder="Type to search customers…"
        autoComplete="off"
      />
      {loading && (
        <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 12, opacity: 0.5, pointerEvents: "none" }}>
          …
        </div>
      )}
      {open && results.length > 0 && (
        <div style={{
          position: "absolute", zIndex: 200, top: "100%", left: 0, right: 0,
          background: "var(--card-bg)", border: "1px solid var(--card-border)",
          borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          maxHeight: 260, overflowY: "auto", marginTop: 2,
        }}>
          {results.map((row) => {
            const name    = row.customer_name || row.name || "(no name)";
            const detail  = [row.city, row.prov || row.province].filter(Boolean).join(", ");
            return (
              <div
                key={row.id}
                onMouseDown={() => select(row)}
                style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid var(--card-border)" }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--btn-outline)"}
                onMouseLeave={e => e.currentTarget.style.background = ""}
              >
                <div style={{ fontWeight: 700, fontSize: 14 }}>{name}</div>
                {detail && <div className="muted" style={{ fontSize: 12 }}>{detail}</div>}
              </div>
            );
          })}
        </div>
      )}
      {open && !loading && results.length === 0 && query.trim().length >= 2 && (
        <div style={{
          position: "absolute", zIndex: 200, top: "100%", left: 0, right: 0,
          background: "var(--card-bg)", border: "1px solid var(--card-border)",
          borderRadius: 8, padding: "10px 14px", marginTop: 2,
        }}>
          <span className="muted" style={{ fontSize: 13 }}>No customers found</span>
        </div>
      )}
    </div>
  );
}

function Field({ label, fieldKey, value, onChange }) {
  // Customer live search
  if (fieldKey === "customer") {
    return <CustomerAutocomplete value={value ?? ""} onChange={onChange} />;
  }

  // ColorCode dropdown
  if (fieldKey === "color_code") {
    const cur = value === null || value === undefined ? "" : String(value).trim();
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
        <select className="input" value={cur} onChange={(e) => onChange(e.target.value)}>
          <option value="">(blank)</option>
          {COLOR_CODE_OPTIONS.map((o) => (
            <option key={o.v} value={o.v}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // unit_category dropdown
  if (fieldKey === "unit_category") {
    const v = (value ?? "").toString().toLowerCase();
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
        <select className="input" value={v} onChange={(e) => onChange(e.target.value)}>
          {CATEGORY_OPTIONS.map((o) => (
            <option key={o.v} value={o.v}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // date picker
  if (DATE_FIELDS.has(fieldKey)) {
    const raw = value ?? "";
    const v = toDateInputValue(raw);
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
        <input type="date" className="input" value={v} onChange={(e) => onChange(e.target.value || "")} />
        {raw && !v && (
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            Current (raw): {String(raw)}
          </div>
        )}
      </div>
    );
  }

  // finance fields always text
  if (CURRENCY_FIELDS.has(fieldKey)) {
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
        <input className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }

  // checkboxes
  if (CHECKBOX_FIELDS.has(fieldKey)) {
    const checked = isTruthyFlag(value);
    return (
      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, whiteSpace: "nowrap" }}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked ? "1" : "0")} />
        {label}
      </label>
    );
  }

  // generic bool-ish dropdown (for other fields)
  if (isBoolishValue(value)) {
    const s = String(value ?? "").toLowerCase();
    const cur =
      s === "1" || s === "true" || s === "yes"
        ? "1"
        : s === "0" || s === "false" || s === "no"
          ? "0"
          : "";
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
        <select className="input" value={cur} onChange={(e) => onChange(e.target.value)}>
          <option value="">(blank)</option>
          <option value="1">true</option>
          <option value="0">false</option>
        </select>
      </div>
    );
  }

  // default text input
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
      <input className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function SortableGroup({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const handleProps = { ...attributes, ...listeners };

  return (
    <div ref={setNodeRef} style={style}>
      {children(handleProps)}
    </div>
  );
}

function GroupCard({ title, items, draft, setDraft, handleProps }) {
  const isClassification = title === "Unit Classification";

  const wrapperStyle = isClassification
    ? { display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }
    : {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 12,
      };

  return (
    <div className="card" style={{ minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <div style={{ fontWeight: 800, letterSpacing: 0.2 }}>{title}</div>

        <button
          type="button"
          {...handleProps}
          style={{
            cursor: "grab",
            userSelect: "none",
            border: "1px solid rgba(0,0,0,0.15)",
            background: "#f5f5f5",
            borderRadius: 8,
            padding: "4px 8px",
            lineHeight: 1,
          }}
          title="Drag to reorder"
          aria-label={`Drag ${title}`}
          onMouseDown={(e) => e.preventDefault()}
        >
          ⠿
        </button>
      </div>

      <div style={wrapperStyle}>
        {items.map(({ label, key }) => (
          <Field
            key={key}
            label={label}
            fieldKey={key}
            value={draft?.[key]}
            onChange={(v) => setDraft((p) => ({ ...p, [key]: v }))}
          />
        ))}
      </div>
    </div>
  );
}

export default function NewRecordPage() {
  const nav = useNavigate();

  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // group order persistence (same vibe as DetailPage)
  const table = "all_inventory";
  const DEFAULT_GROUP_ORDER = GROUPS.map((g) => g.title).concat(["__remaining__"]);
  const ORDER_KEY = `new_group_order:${table}`;

  const [groupOrder, setGroupOrder] = useState(() => {
    try {
      const raw = sessionStorage.getItem(ORDER_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) return parsed;
    } catch {}
    return DEFAULT_GROUP_ORDER;
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(ORDER_KEY, JSON.stringify(groupOrder));
    } catch {}
  }, [groupOrder, ORDER_KEY]);

  // load columns (robust) and initialize blank draft
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setErr("");
        const { data } = await api.get("/all_inventory");
        if (!alive) return;

        const cols = data?.[0] ? Object.keys(data[0]) : [];
        const empty = {};
        for (const c of cols) empty[c] = "";

        // defaults that feel sane on create
        if (Object.prototype.hasOwnProperty.call(empty, "alert")) empty.alert = "0";
        for (const k of CHECKBOX_FIELDS) {
          if (Object.prototype.hasOwnProperty.call(empty, k)) empty[k] = empty[k] || "0";
        }

        setDraft(empty);
      } catch (e) {
        console.error("Load columns failed", e);
        if (!alive) return;
        setErr("Failed to load columns (check console/network).");
        setDraft({});
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const resolvedGroups = useMemo(() => {
    if (!draft) return [];
    return GROUPS.map((g) => {
      const items = [];
      for (const f of g.fields) {
        const resolvedKey = findKey(draft, f.k);
        if (!resolvedKey) continue;
        items.push({ label: f.label, key: resolvedKey });
      }
      return { title: g.title, items };
    }).filter((g) => g.items.length > 0);
  }, [draft]);

  const remaining = useMemo(() => {
    if (!draft) return [];
    const used = new Set(["id"]);
    for (const g of resolvedGroups) for (const it of g.items) used.add(it.key);

    return Object.keys(draft)
      .filter((k) => !used.has(k))
      .sort((a, b) => a.localeCompare(b));
  }, [draft, resolvedGroups]);

  const orderedItems = useMemo(() => {
    const byId = new Map();
    for (const g of resolvedGroups) byId.set(g.title, { type: "group", g });
    byId.set("__remaining__", { type: "remaining" });

    const existing = groupOrder.filter((id) => byId.has(id));
    for (const [id] of byId) if (!existing.includes(id)) existing.push(id);

    return existing.map((id) => ({ id, ...byId.get(id) }));
  }, [groupOrder, resolvedGroups]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function onDragEnd(event) {
    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;

    setGroupOrder((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }

  const clearAll = () => {
    if (!draft) return;
    const empty = {};
    for (const k of Object.keys(draft)) empty[k] = "";

    // keep checkboxes sane
    if (Object.prototype.hasOwnProperty.call(empty, "alert")) empty.alert = "0";
    for (const k of CHECKBOX_FIELDS) {
      if (Object.prototype.hasOwnProperty.call(empty, k)) empty[k] = "0";
    }

    setDraft(empty);
    setMsg("");
    setErr("");
  };

  const buildCreatePayload = () => {
    const payload = { ...(draft || {}) };

    // never send id on create
    delete payload.id;

    // normalize checkbox flags to "0"/"1"
    for (const k of CHECKBOX_FIELDS) {
      if (payload[k] === undefined) continue;
      payload[k] = isTruthyFlag(payload[k]) ? "1" : "0";
    }

    // trim strings (avoid "   " spam)
    for (const [k, v] of Object.entries(payload)) {
      if (v == null) continue;
      if (typeof v === "string") payload[k] = v.trim();
    }

    // optional: drop empty strings to let DB defaults apply (if any)
    // (keep flags though)
    for (const k of Object.keys(payload)) {
      if (CHECKBOX_FIELDS.has(k)) continue;
      if (payload[k] === "") delete payload[k];
    }

    return payload;
  };

  const submit = async () => {
    setBusy(true);
    setMsg("");
    setErr("");
    try {
      const payload = buildCreatePayload();

      // IMPORTANT:
      // Your old NewRecordPage used `/api/inventory` which is probably wrong with your axios baseURL '/api'.
      // In your app, your REST pattern elsewhere is `/${table}`.
      // So we create by POSTing to `/${table}`.
      const { data } = await api.post(`/${table}`, payload);

      const newId = data?.id;
      setMsg(newId ? `Created id ${newId}` : "Created");

      // bounce to details like a proper workflow
      if (newId) nav(`/detail/${table}/${newId}`);
    } catch (e) {
      console.error("Create failed", e);
      setErr(e?.response?.data?.error || "Create failed (check console/network).");
    } finally {
      setBusy(false);
    }
  };

  if (!draft) return <div className="container">Loading…</div>;

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 800, letterSpacing: 0.3 }}>New Record</h2>
          <div className="muted" style={{ marginTop: 4 }}>
            Creates a new row in <b>{table}</b>. Same layout as Details.
          </div>
        </div>

        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <button className="btn-outline" onClick={() => nav(-1)} disabled={busy}>
            Back
          </button>
          <button className="btn-outline" onClick={clearAll} disabled={busy}>
            Clear All
          </button>
          <button className="btn" onClick={submit} disabled={busy}>
            {busy ? "Saving…" : "Submit"}
          </button>
        </div>
      </div>

      {err ? <div className="muted" style={{ marginTop: 8 }}>{err}</div> : null}
      {msg ? <div className="muted" style={{ marginTop: 8 }}>{msg}</div> : null}

      {/* No "Related Records" on create — but keep the same vertical rhythm */}
      <div className="card" style={{ minWidth: 0, marginTop: 12 }}>
        <div style={{ fontWeight: 800, letterSpacing: 0.2, marginBottom: 6 }}>Create Tips</div>
        <div className="muted" style={{ lineHeight: 1.6 }}>
          Start with <b>Customer</b>, <b>RO/WO</b> or <b>Stock PO</b>, then classification + dates.
          Checkboxes save as <code>"0"</code>/<code>"1"</code>.
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={orderedItems.map((x) => x.id)} strategy={verticalListSortingStrategy}>
          <div
            style={{
              columnWidth: 360,
              columnGap: 12,
              alignItems: "start",
              marginTop: 12,
            }}
          >
            {orderedItems.map((item) => {
              if (item.type === "group") {
                return (
                  <SortableGroup key={item.id} id={item.id}>
                    {(handleProps) => (
                      <div
                        style={{
                          breakInside: "avoid",
                          WebkitColumnBreakInside: "avoid",
                          marginBottom: 12,
                          width: "100%",
                          display: "inline-block",
                        }}
                      >
                        <GroupCard
                          title={item.g.title}
                          items={item.g.items}
                          draft={draft}
                          setDraft={setDraft}
                          handleProps={handleProps}
                        />
                      </div>
                    )}
                  </SortableGroup>
                );
              }

              // remaining
              return (
                <SortableGroup key={item.id} id={item.id}>
                  {(handleProps) => (
                    <div
                      className="card"
                      style={{
                        breakInside: "avoid",
                        WebkitColumnBreakInside: "avoid",
                        marginBottom: 12,
                        width: "100%",
                        display: "inline-block",
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          marginBottom: 10,
                        }}
                      >
                        <div style={{ fontWeight: 800, letterSpacing: 0.2 }}>Remaining Fields</div>

                        <button
                          type="button"
                          {...handleProps}
                          style={{
                            cursor: "grab",
                            userSelect: "none",
                            border: "1px solid rgba(0,0,0,0.15)",
                            background: "#f5f5f5",
                            borderRadius: 8,
                            padding: "4px 8px",
                            lineHeight: 1,
                          }}
                          title="Drag to reorder"
                          aria-label="Drag Remaining Fields"
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          ⠿
                        </button>
                      </div>

                      {remaining.length === 0 ? (
                        <div className="muted">Nothing extra.</div>
                      ) : (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: 12,
                          }}
                        >
                          {remaining.map((k) => (
                            <Field
                              key={k}
                              label={k}
                              fieldKey={k}
                              value={draft?.[k]}
                              onChange={(v) => setDraft((p) => ({ ...p, [k]: v }))}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </SortableGroup>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
